/**
 * Outlook Calendar integration page.
 *
 * Reuses the existing Microsoft 365 Graph connection — no separate OAuth needed.
 * Customers configure:
 *   - Organizer email (whose calendar gets booked)
 *   - Default meeting duration, title prefix, description template
 *   - Optional meeting link (Teams/Zoom/Meet)
 *
 * The "Book Appointment" capability is exposed to bots via a workflow HTTP node
 * pointing to POST /api/admin/OutlookCalendar/Book.
 *
 * AZURE NOTE: The Velaro Azure AD app must have Calendars.ReadWrite application permission.
 * Add it in Azure Portal → App Registrations → Velaro Admin → API Permissions,
 * then re-grant tenant admin consent (or the next customer who clicks Connect M365 will get it).
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationActivityLog from "../IntegrationActivityLog";

// Shared API type alias (imported from hook return type)
type Api = ReturnType<typeof useApi>;

// ── Booking history ────────────────────────────────────────────────────────

interface BookingRecord {
  id: number;
  conversationId: string;
  visitorName: string;
  visitorEmail: string;
  provider: string;
  eventId: string;
  eventUrl: string;
  title: string;
  startUtc: string | null;
  endUtc: string | null;
  status: string;
  createdAt: string;
}

function BookingHistory({ api }: { api: Api }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [total, setTotal]     = useState(0);
  const [cancelling, setCancelling] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res  = await api.admin.get("OutlookCalendar/BookingHistory?size=10");
      const data = await res.json();
      setBookings(Array.isArray(data.results) ? data.results : []);
      setTotal(data.total ?? 0);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }

  async function cancel(booking: BookingRecord) {
    if (!confirm(`Cancel "${booking.title}"? This will delete the calendar event for all attendees.`)) return;
    setCancelling(booking.id);
    try {
      const res  = await api.admin.delete(`OutlookCalendar/CancelEvent?eventId=${encodeURIComponent(booking.eventId)}`);
      const data = await res.json();
      if (data.success) await load();
      else alert(data.error || "Cancellation failed.");
    } catch {
      alert("Cancellation failed.");
    } finally {
      setCancelling(null);
    }
  }

  function handleOpen() {
    setOpen(true);
    if (bookings.length === 0) load();
  }

  const STATUS_COLORS: Record<string, string> = {
    active:    "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    pending:   "bg-amber-100 text-amber-700",
    completed: "bg-slate-100 text-slate-600"
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => open ? setOpen(false) : handleOpen()}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          Booking history
          {total > 0 && (
            <span className="text-[11px] bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded-full">{total} total</span>
          )}
        </span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="border-t border-slate-100">
          {loading ? (
            <p className="px-6 py-4 text-xs text-slate-400">Loading...</p>
          ) : bookings.length === 0 ? (
            <p className="px-6 py-4 text-xs text-slate-400">No appointments booked yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-slate-500 font-medium">
                    <th className="px-4 py-2">Visitor</th>
                    <th className="px-4 py-2">Meeting</th>
                    <th className="px-4 py-2">Time (UTC)</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-2">
                        <div className="font-medium text-slate-700">{b.visitorName || "—"}</div>
                        <div className="text-slate-400">{b.visitorEmail}</div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-slate-700 max-w-[200px] truncate">{b.title}</div>
                        {b.eventUrl && (
                          <a href={b.eventUrl} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline text-[10px]">
                            View in Outlook →
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-500">
                        {b.startUtc ? new Date(b.startUtc).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC" : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${STATUS_COLORS[b.status] ?? "bg-slate-100 text-slate-600"}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {b.status === "active" && b.eventId && (
                          <button
                            className="text-[10px] text-red-500 hover:underline disabled:opacity-50"
                            onClick={() => cancel(b)}
                            disabled={cancelling === b.id}
                          >
                            {cancelling === b.id ? "Cancelling..." : "Cancel"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > 10 && (
                <div className="px-4 py-2 text-xs text-slate-400">
                  Showing 10 of {total} bookings.
                </div>
              )}
            </div>
          )}
          <div className="px-6 py-2 border-t border-slate-100">
            <button className="text-xs text-blue-500 hover:underline" onClick={load}>Refresh</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Appointment Types ──────────────────────────────────────────────────────

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_BITS   = [1, 2, 4, 8, 16, 32, 64];
const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => ({
  value: i,
  label: i === 0 ? "12:00 AM" : i < 12 ? `${i}:00 AM` : i === 12 ? "12:00 PM" : `${i - 12}:00 PM`
}));

interface TimeBlock { start: number; end: number; }

interface AppointmentType {
  id: number;
  name: string;
  description: string;
  organizerEmail: string;
  organizerName: string;
  durationMinutes: number;
  timeBlocks: TimeBlock[];
  workingDays: number;
  timezone: string;
  leadTimeHours: number;
  maxHorizonDays: number;
  bufferMinutes: number;
  randomSlotCount: number;
  titlePrefix: string;
  meetingLinkTemplate: string;
  notifyOrganizer: boolean;
  sendVisitorEmail: boolean;
  isEnabled: boolean;
}

interface ScheduleOverride {
  id: number;
  appointmentTypeId: number | null;
  weekStartDate: string;
  isRecurring: boolean;
  workingDays: number | null;
  timeBlocks: TimeBlock[];
  note: string;
}

const BLANK_TYPE: Omit<AppointmentType, "id" | "isEnabled"> = {
  name: "", description: "", organizerEmail: "", organizerName: "",
  durationMinutes: 30, timeBlocks: [{ start: 9, end: 17 }],
  workingDays: 31, timezone: "UTC", leadTimeHours: 0, maxHorizonDays: 90,
  bufferMinutes: 0, randomSlotCount: 0, titlePrefix: "", meetingLinkTemplate: "",
  notifyOrganizer: true, sendVisitorEmail: true
};

function workingDaysLabel(bits: number) {
  return DAY_BITS.map((b, i) => (bits & b) ? DAY_LABELS[i] : null).filter(Boolean).join(", ") || "None";
}

function hourLabel(h: number) { return HOUR_OPTIONS.find(o => o.value === h)?.label ?? `${h}:00`; }

function TimeBlocksEditor({ blocks, onChange }: { blocks: TimeBlock[]; onChange: (b: TimeBlock[]) => void }) {
  function addBlock() { onChange([...blocks, { start: 9, end: 17 }]); }
  function removeBlock(i: number) { onChange(blocks.filter((_, idx) => idx !== i)); }
  function updateBlock(i: number, key: keyof TimeBlock, val: number) {
    onChange(blocks.map((b, idx) => idx === i ? { ...b, [key]: val } : b));
  }
  return (
    <div>
      {blocks.map((block, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <select
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            value={block.start}
            onChange={e => updateBlock(i, "start", Number(e.target.value))}>
            {HOUR_OPTIONS.slice(0, 24).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="text-slate-400 text-xs flex-shrink-0">to</span>
          <select
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            value={block.end}
            onChange={e => updateBlock(i, "end", Number(e.target.value))}>
            {HOUR_OPTIONS.slice(1).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {blocks.length > 1 ? (
            <button type="button" className="text-slate-400 hover:text-red-500 text-base leading-none flex-shrink-0 w-5 text-center"
              onClick={() => removeBlock(i)} aria-label="Remove block">×</button>
          ) : <div className="w-5" />}
        </div>
      ))}
      <button type="button" className="text-xs text-blue-600 hover:underline mt-1" onClick={addBlock}>
        + Add another time block
      </button>
    </div>
  );
}

function getMondayOfWeek(d: Date) {
  const date = new Date(d);
  const day  = date.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
}

function ScheduleOverrideModal({
  initial, appointmentTypeId, onSave, onClose
}: {
  initial: Partial<ScheduleOverride> | null;
  appointmentTypeId: number;
  onSave: (data: Omit<ScheduleOverride, "id">) => Promise<void>;
  onClose: () => void;
}) {
  const editing = initial && (initial as ScheduleOverride).id != null;
  const defaultWeek = getMondayOfWeek(new Date(Date.now() + 7 * 86400000)); // next week
  const [form, setForm] = React.useState({
    weekStartDate: initial?.weekStartDate ?? defaultWeek,
    isRecurring: initial?.isRecurring ?? false,
    workingDays: initial?.workingDays ?? null as number | null,
    timeBlocks: initial?.timeBlocks ?? [{ start: 9, end: 17 }] as TimeBlock[],
    note: initial?.note ?? ""
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError]   = React.useState<string | null>(null);

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await onSave({ ...form, appointmentTypeId });
      onClose();
    } catch { setError("Save failed."); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 text-sm">{editing ? "Edit" : "Add"} schedule override</h3>
          <button className="text-slate-400 hover:text-slate-700 text-lg leading-none" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Instructions */}
          <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
            An override replaces this type's normal availability for a specific week — or from that week onwards. Use it when your schedule changes temporarily (vacation, event) or permanently (new recurring hours).
          </div>

          {/* Week */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Week starting</label>
            <input type="date"
              className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={form.weekStartDate}
              onChange={e => setForm(f => ({ ...f, weekStartDate: getMondayOfWeek(new Date(e.target.value)) }))} />
            <p className="text-[10px] text-slate-400 mt-0.5">Date auto-rounds to the Monday of that week.</p>
          </div>

          {/* Recurrence */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Applies to</label>
            <div className="flex gap-2">
              {([false, true] as const).map(rec => (
                <button key={String(rec)} type="button"
                  className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-colors ${form.isRecurring === rec ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}
                  onClick={() => setForm(f => ({ ...f, isRecurring: rec }))}>
                  {rec ? "This week onwards (new baseline)" : "Just this week (one-off)"}
                </button>
              ))}
            </div>
            {form.isRecurring && (
              <p className="text-[10px] text-amber-600 mt-1.5">
                This becomes the permanent schedule from this week forward, until you add a newer recurring override.
              </p>
            )}
          </div>

          {/* Working days (optional) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-600">Available days <span className="text-slate-400">(optional)</span></label>
              <button type="button" className="text-[10px] text-slate-400 hover:text-slate-600"
                onClick={() => setForm(f => ({ ...f, workingDays: f.workingDays == null ? 31 : null }))}>
                {form.workingDays == null ? "Override days" : "Inherit from type"}
              </button>
            </div>
            {form.workingDays != null ? (
              <div className="flex gap-1.5 flex-wrap">
                {DAY_LABELS.map((label, i) => {
                  const active = ((form.workingDays ?? 0) & DAY_BITS[i]) !== 0;
                  return (
                    <button key={label} type="button"
                      className={`px-2.5 py-1 rounded text-xs font-medium border ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}
                      onClick={() => setForm(f => ({ ...f, workingDays: (f.workingDays ?? 0) ^ DAY_BITS[i] }))}>
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Inheriting available days from appointment type.</p>
            )}
          </div>

          {/* Time blocks */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Available hours this week</label>
            <p className="text-[10px] text-slate-400 mb-2">
              Add multiple time windows for the same day — e.g. 9–10 AM and 2–5 PM. These replace the type's normal hours for this override period.
            </p>
            <TimeBlocksEditor blocks={form.timeBlocks} onChange={b => setForm(f => ({ ...f, timeBlocks: b }))} />
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Note <span className="text-slate-400">(internal only)</span></label>
            <input className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g. Alex at conference, updated Q2 schedule"
              value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" onClick={onClose}>Cancel</button>
          <button
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editing ? "Update override" : "Add override"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleOverridesPanel({ api, typeId, onClose }: { api: Api; typeId: number; onClose: () => void }) {
  const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState<Partial<ScheduleOverride> | null | false>(false);
  const [deleting, setDeleting]   = useState<number | null>(null);

  async function load() {
    try {
      const res  = await api.admin.get(`OutlookCalendar/ScheduleOverrides?appointmentTypeId=${typeId}`);
      const data = await res.json();
      setOverrides(Array.isArray(data.overrides) ? data.overrides : []);
    } catch { setOverrides([]); }
    finally  { setLoading(false); }
  }

  useEffect(() => { load(); }, [typeId]);

  async function handleSave(data: Omit<ScheduleOverride, "id">) {
    const editing = modal && (modal as ScheduleOverride).id != null;
    if (editing) await api.admin.put(`OutlookCalendar/ScheduleOverrides/${(modal as ScheduleOverride).id}`, data);
    else         await api.admin.post("OutlookCalendar/ScheduleOverrides", data);
    await load();
  }

  async function handleDelete(id: number) {
    setDeleting(id);
    try { await api.admin.delete(`OutlookCalendar/ScheduleOverrides/${id}`); await load(); }
    catch { /* ignore */ }
    finally { setDeleting(null); }
  }

  const formatWeek = (s: string) => {
    const d = new Date(s + "T00:00:00Z");
    const e = new Date(d); e.setDate(d.getDate() + 6);
    return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}`;
  };

  const isThisWeek = (s: string) => getMondayOfWeek(new Date()) === s;
  const isPast     = (s: string) => new Date(s + "T00:00:00Z") < new Date(getMondayOfWeek(new Date()) + "T00:00:00Z");

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-800">Schedule overrides</h2>
            <p className="text-xs text-slate-400 mt-0.5">Change availability for a specific week or permanently from a week onwards.</p>
          </div>
          <button className="text-slate-400 hover:text-slate-700 text-lg leading-none" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="px-6 py-5">
          {loading ? (
            <p className="text-xs text-slate-400">Loading...</p>
          ) : overrides.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-5 text-center mb-4">
              <p className="text-sm text-slate-500 font-medium mb-1">No overrides yet</p>
              <p className="text-xs text-slate-400">
                Use overrides when your availability differs from the default — for a vacation week, a conference, or a permanent new schedule.
              </p>
            </div>
          ) : (
            <div className="space-y-2 mb-4">
              {overrides.map(o => {
                const past = isPast(o.weekStartDate);
                const thisWeek = isThisWeek(o.weekStartDate);
                const blocksText = o.timeBlocks.map(b => `${hourLabel(b.start)}–${hourLabel(b.end)}`).join(", ");
                return (
                  <div key={o.id} className={`rounded-lg border px-4 py-3 ${past ? "opacity-50 bg-slate-50 border-slate-200" : o.isRecurring ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-slate-700">{formatWeek(o.weekStartDate)}</span>
                          {thisWeek && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Current week</span>}
                          {o.isRecurring && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Recurring from here</span>}
                          {past && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Past</span>}
                        </div>
                        <p className="text-[11px] text-slate-600 mt-0.5">{blocksText || "No availability"}</p>
                        {o.workingDays != null && <p className="text-[10px] text-slate-400">{workingDaysLabel(o.workingDays)} only</p>}
                        {o.note && <p className="text-[10px] text-slate-400 italic mt-0.5">{o.note}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button className="text-xs text-blue-600 hover:underline" onClick={() => setModal(o)}>Edit</button>
                        <button className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                          onClick={() => handleDelete(o.id)} disabled={deleting === o.id}>
                          {deleting === o.id ? "..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <button
            className="w-full py-2 border border-dashed border-blue-300 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-50"
            onClick={() => setModal({})}>
            + Add override
          </button>
        </div>
      </div>

      {modal !== false && (
        <ScheduleOverrideModal
          initial={modal || null}
          appointmentTypeId={typeId}
          onSave={handleSave}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}

function AppointmentTypeModal({
  initial, onSave, onClose
}: {
  initial: Partial<AppointmentType> | null;
  onSave: (data: Omit<AppointmentType, "id" | "isEnabled">) => Promise<void>;
  onClose: () => void;
}) {
  const editing = initial && (initial as AppointmentType).id != null;
  const [form, setForm] = React.useState<Omit<AppointmentType, "id" | "isEnabled">>({
    ...BLANK_TYPE, ...(initial ?? {}),
    // Backwards compat: if loaded type has no timeBlocks, build from start/end
    timeBlocks: (initial as any)?.timeBlocks?.length > 0
      ? (initial as any).timeBlocks
      : [{ start: (initial as any)?.workingHoursStart ?? 9, end: (initial as any)?.workingHoursEnd ?? 17 }]
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError]   = React.useState<string | null>(null);

  function set<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function toggleDay(bit: number) {
    setForm(f => ({ ...f, workingDays: f.workingDays ^ bit }));
  }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    if (form.timeBlocks.some(b => b.start >= b.end)) { setError("Each time block must have a start time before its end time."); return; }
    setSaving(true); setError(null);
    try { await onSave(form); onClose(); }
    catch { setError("Save failed. Please try again."); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800">{editing ? "Edit" : "New"} appointment type</h2>
          <button className="text-slate-400 hover:text-slate-700 text-lg leading-none" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Name + description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Name <span className="text-red-500">*</span></label>
            <input className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g. Product Demo, Support Call"
              value={form.name} onChange={e => set("name", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description <span className="text-slate-400">(optional)</span></label>
            <input className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g. 60-minute product walkthrough"
              value={form.description} onChange={e => set("description", e.target.value)} />
          </div>

          {/* Organizer */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Organizer</p>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                <strong>Leave blank</strong> → all bookings go to the shared company calendar (global organizer email). Someone on the team gets assigned the lead via rules.<br />
                <strong>Set a specific email</strong> → bookings go directly to that person's Outlook calendar. Great for assigning a dedicated sales or support rep.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Organizer email</label>
                <input className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="demos@company.com"
                  value={form.organizerEmail} onChange={e => set("organizerEmail", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display name</label>
                <input className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Sales Team"
                  value={form.organizerName} onChange={e => set("organizerName", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Duration</label>
            <select className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              value={form.durationMinutes} onChange={e => set("durationMinutes", Number(e.target.value))}>
              {[15, 20, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} minutes</option>)}
            </select>
          </div>

          {/* Availability */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-4">
            <p className="text-xs font-semibold text-slate-600">Default availability</p>
            <p className="text-[11px] text-slate-400 -mt-2 leading-relaxed">
              This is the baseline schedule. Use <strong>Schedule Overrides</strong> (on the type card) to change it for a specific week or permanently from a certain date.
            </p>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">Available days</label>
              <div className="flex gap-1.5 flex-wrap">
                {DAY_LABELS.map((label, i) => {
                  const active = (form.workingDays & DAY_BITS[i]) !== 0;
                  return (
                    <button key={label} type="button"
                      className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}
                      onClick={() => toggleDay(DAY_BITS[i])}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">
                Available hours
                <span className="text-slate-400 font-normal ml-1">— add multiple blocks for split-day schedules (e.g. 9–10 AM and 2–5 PM)</span>
              </label>
              <TimeBlocksEditor
                blocks={form.timeBlocks}
                onChange={blocks => set("timeBlocks", blocks)} />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Timezone</label>
              <input className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="UTC"
                value={form.timezone} onChange={e => set("timezone", e.target.value)} />
              <p className="text-[10px] text-slate-400 mt-0.5">IANA timezone (e.g. America/New_York, Europe/London)</p>
            </div>
          </div>

          {/* Booking rules */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600">Booking rules</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Min. notice</label>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="720"
                    className="w-16 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.leadTimeHours} onChange={e => set("leadTimeHours", Number(e.target.value))} />
                  <span className="text-xs text-slate-400">hrs</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">0 = same-day. 72 = 3-day advance required.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Book up to</label>
                <div className="flex items-center gap-1">
                  <input type="number" min="1" max="365"
                    className="w-16 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.maxHorizonDays} onChange={e => set("maxHorizonDays", Number(e.target.value))} />
                  <span className="text-xs text-slate-400">days out</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Buffer after</label>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="120"
                    className="w-16 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.bufferMinutes} onChange={e => set("bufferMinutes", Number(e.target.value))} />
                  <span className="text-xs text-slate-400">min gap</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">Gap after each booking.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Random slots</label>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="20"
                    className="w-16 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.randomSlotCount} onChange={e => set("randomSlotCount", Number(e.target.value))} />
                  <span className="text-xs text-slate-400">slots</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">0 = show all. 2 = show 2 random slots (creates scarcity).</p>
              </div>
            </div>
          </div>

          {/* Overrides */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title prefix <span className="text-slate-400">(override global)</span></label>
            <input className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Demo with"
              value={form.titlePrefix} onChange={e => set("titlePrefix", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Meeting link <span className="text-slate-400">(override global)</span></label>
            <input className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="https://teams.microsoft.com/..."
              value={form.meetingLinkTemplate} onChange={e => set("meetingLinkTemplate", e.target.value)} />
          </div>

          {/* Notifications */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600">Notifications</p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5 rounded"
                checked={form.sendVisitorEmail}
                onChange={e => set("sendVisitorEmail", e.target.checked)} />
              <span className="text-xs text-slate-700">
                <span className="font-medium">Send visitor confirmation email</span>
                <span className="ml-1 text-slate-400">— Mandrill email to the visitor right after booking with meeting title, date, and time.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5 rounded"
                checked={form.notifyOrganizer}
                onChange={e => set("notifyOrganizer", e.target.checked)} />
              <span className="text-xs text-slate-700">
                <span className="font-medium">Notify organizer on booking</span>
                <span className="ml-1 text-slate-400">— Mandrill email to the organizer with visitor details and a direct calendar link.</span>
              </span>
            </label>
            <div className="pt-1 border-t border-slate-200 space-y-1.5">
              <p className="text-[11px] font-medium text-slate-500">Coming soon</p>
              <p className="text-[11px] text-slate-400">
                <span className="font-medium text-slate-500">Visitor SMS confirmation</span> — requires ACS phone number setup under Integrations → SMS.
              </p>
              <p className="text-[11px] text-slate-400">
                <span className="font-medium text-slate-500">Day-before reminder</span> — set up a scheduled workflow in Workflows to send a reminder the day before the appointment using the <code className="bg-slate-100 px-1 rounded">{"<<bookingEventId>>"}</code> variable.
              </p>
            </div>
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" onClick={onClose}>Cancel</button>
          <button
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editing ? "Save changes" : "Create type"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AppointmentTypesSection({ api, globalOrganizerEmail }: { api: Api; globalOrganizerEmail: string }) {
  const [types, setTypes]             = useState<AppointmentType[]>([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState<Partial<AppointmentType> | null | false>(false);
  const [overridesTypeId, setOverridesTypeId] = useState<number | null>(null);
  const [deleting, setDeleting]       = useState<number | null>(null);

  async function load() {
    try {
      const res  = await api.admin.get("OutlookCalendar/AppointmentTypes");
      const data = await res.json();
      setTypes(Array.isArray(data.types) ? data.types : []);
    } catch { setTypes([]); }
    finally  { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleSave(data: Omit<AppointmentType, "id" | "isEnabled">) {
    const editing = modal && (modal as AppointmentType).id != null;
    if (editing) {
      await api.admin.put(`OutlookCalendar/AppointmentTypes/${(modal as AppointmentType).id}`, data);
    } else {
      await api.admin.post("OutlookCalendar/AppointmentTypes", data);
    }
    await load();
  }

  async function handleDelete(id: number, name: string) {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await api.admin.delete(`OutlookCalendar/AppointmentTypes/${id}`);
      await load();
    } catch { /* ignore */ }
    finally { setDeleting(null); }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-slate-700">Appointment types</p>
          <p className="text-xs text-slate-400 mt-0.5">
            Define named booking slots with per-type rules — different organizers, durations, and availability windows.
            Use the type's <code className="bg-slate-100 px-1 rounded text-[10px]">id</code> in the bot workflow to apply the right rules automatically.
          </p>
        </div>
        <button
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700"
          onClick={() => setModal({})}
        >
          + Add type
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Loading...</p>
      ) : types.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center">
          <p className="text-sm text-slate-500 font-medium">No appointment types yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-3">
            Add types to define different availability windows per meeting — demos need 3 days notice, support calls can be same-day, and so on.
          </p>
          <button className="text-xs text-blue-600 hover:underline font-medium" onClick={() => setModal({})}>
            + Create your first appointment type
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {types.map(t => {
            const daysLabel  = workingDaysLabel(t.workingDays);
            const blocks     = Array.isArray(t.timeBlocks) && t.timeBlocks.length > 0 ? t.timeBlocks : [{ start: 9, end: 17 }];
            const hoursLabel = blocks.map(b => `${hourLabel(b.start)}–${hourLabel(b.end)}`).join(", ");
            const organizer  = t.organizerEmail || globalOrganizerEmail || "—";
            return (
              <div key={t.id} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{t.name}</span>
                      <span className="text-[11px] bg-blue-100 text-blue-700 font-medium px-2 py-0.5 rounded-full">{t.durationMinutes} min</span>
                      {t.leadTimeHours > 0 && (
                        <span className="text-[11px] bg-amber-100 text-amber-700 font-medium px-2 py-0.5 rounded-full">{t.leadTimeHours}h notice</span>
                      )}
                      {t.randomSlotCount > 0 && (
                        <span className="text-[11px] bg-purple-100 text-purple-700 font-medium px-2 py-0.5 rounded-full">{t.randomSlotCount} random slots</span>
                      )}
                      {t.bufferMinutes > 0 && (
                        <span className="text-[11px] bg-slate-200 text-slate-600 font-medium px-2 py-0.5 rounded-full">{t.bufferMinutes}min gap</span>
                      )}
                    </div>
                    {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[11px] text-slate-500">
                      <span>📧 {organizer}</span>
                      <span>📅 {daysLabel}</span>
                      <span>🕐 {hoursLabel}</span>
                      <span>🌐 {t.timezone}</span>
                      <span>↔ Up to {t.maxHorizonDays}d out</span>
                      <span className="text-[10px] text-slate-400">ID: {t.id}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="text-xs text-slate-600 hover:text-blue-600 border border-slate-200 rounded px-2 py-1 hover:border-blue-300"
                      onClick={() => setOverridesTypeId(t.id)}>
                      Schedule
                    </button>
                    <button className="text-xs text-blue-600 hover:underline" onClick={() => setModal(t)}>Edit</button>
                    <button
                      className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                      onClick={() => handleDelete(t.id, t.name)}
                      disabled={deleting === t.id}>
                      {deleting === t.id ? "..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-[11px] text-blue-700 mt-2">
            In the bot workflow, pass <code className="bg-blue-100 px-1 rounded">appointmentTypeId</code> to <code className="bg-blue-100 px-1 rounded">/OutlookCalendar/AvailableSlots</code> to get slots filtered by this type's rules. Then pass the same ID to <code className="bg-blue-100 px-1 rounded">/OutlookCalendar/Book</code> to override the organizer and duration automatically.
          </div>
        </div>
      )}

      {modal !== false && (
        <AppointmentTypeModal
          initial={modal || null}
          onSave={handleSave}
          onClose={() => setModal(false)}
        />
      )}

      {overridesTypeId != null && (
        <ScheduleOverridesPanel
          api={api}
          typeId={overridesTypeId}
          onClose={() => setOverridesTypeId(null)}
        />
      )}
    </div>
  );
}

// ── How availability rules work (instructions panel) ──────────────────────────

function HowAvailabilityWorks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <button type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <span>How availability rules work</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 mt-4 space-y-5 text-sm text-slate-600">

          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Layers of availability control</p>
            {[
              {
                n: "1",
                title: "Appointment type — default schedule",
                body: "Each type defines its baseline: which days (Mon–Fri), which hours (9 AM–5 PM), and whether there are multiple time windows per day (e.g. 9–10 AM and 2–5 PM). This is what visitors see every normal week."
              },
              {
                n: "2",
                title: "Schedule overrides — week-specific changes",
                body: "Click \"Schedule\" on any type to add a one-off override. Example: next week you're available 2–8 PM only because you're at an event. Set WeekStartDate = next Monday, time blocks = [14:00–20:00], and visitors that week only see those slots. The week after, normal schedule resumes automatically."
              },
              {
                n: "3",
                title: "Recurring overrides — permanent new schedule",
                body: "If your schedule changes from a certain week forward, add a recurring override. Example: from March 16 onwards, your new hours are 9–10 AM and 1–4 PM. All weeks from March 16 onward use those blocks — until you add a newer recurring override."
              },
              {
                n: "4",
                title: "Outlook free/busy always takes priority",
                body: "Even within your defined availability windows, any slot where the organizer already has a calendar event shows as unavailable. Velaro queries the Graph API in real time — you never need to block out time manually."
              },
              {
                n: "5",
                title: "Random slots — create a premium feel",
                body: "Set \"Random slots\" to e.g. 2 on your Product Demo type. Instead of showing every open slot, visitors see 2 randomly-chosen available times. This creates scarcity and signals high demand — particularly useful for smaller teams."
              }
            ].map(step => (
              <div key={step.n} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold mt-0.5">{step.n}</span>
                <div>
                  <p className="text-xs font-semibold text-slate-700">{step.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-700">Company mode vs. per-agent mode</p>
            <div className="space-y-1 text-xs text-slate-500">
              <p><strong>Company mode</strong> (recommended for most teams): Leave the organizer email blank in the appointment type. All bookings land on the shared company calendar (configured in the global settings above). Your team assigns the booking to an agent via a Rule → assign conversation → notify agent by email/SMS.</p>
              <p><strong>Per-agent mode</strong>: Set a specific organizer email per type (e.g. demos@company.com, sarah@company.com). Each type books directly to that person's Outlook. The organizer gets an email notification with the visitor's name, time, and a direct link to the calendar event.</p>
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-700">In the bot workflow</p>
            <div className="bg-slate-900 rounded px-3 py-2 text-[11px] font-mono text-slate-200">
              <div className="text-slate-400 mb-1">// GET /api/admin/OutlookCalendar/AvailableSlots</div>
              {`?date=2026-03-20&appointmentTypeId=3`}
            </div>
            <p className="text-xs text-slate-500">Pass <code className="bg-slate-100 px-1 rounded text-[10px]">appointmentTypeId</code> to get slots filtered by that type's rules (working days, hours, lead time, horizon, overrides). The ID is shown on each type card. Then pass the same ID to <code className="bg-slate-100 px-1 rounded text-[10px]">/Book</code> to auto-apply the type's organizer and duration.</p>
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-700">
            <strong>Timezone note:</strong> All times returned by the API are UTC. The bot should display them in the visitor's timezone using <code>{"<<visitorTimezone>>"}</code> if available, or ask the visitor their timezone and format accordingly.
          </div>
        </div>
      )}
    </div>
  );
}

interface CalendarStatus {
  m365Connected: boolean;
  m365TenantId?: string;
  configured: boolean;
  organizerEmail: string;
  organizerName: string;
  defaultDuration: number;
  titlePrefix: string;
  descriptionTemplate: string;
  meetingLinkTemplate: string;
  sendVisitorInvite: boolean;
  sendChatConfirmation: boolean;
}

interface CalendarEntry {
  id: string;
  name: string;
  isDefaultCalendar: boolean;
}

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

export default function OutlookCalendarPage() {
  const api = useApi();

  const [status, setStatus] = useState<CalendarStatus>({
    m365Connected: false,
    configured: false,
    organizerEmail: "",
    organizerName: "",
    defaultDuration: 30,
    titlePrefix: "Meeting with",
    descriptionTemplate: "This meeting was booked via Velaro chat.",
    meetingLinkTemplate: "",
    sendVisitorInvite: true,
    sendChatConfirmation: true
  });
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [testing, setTesting]         = useState(false);
  const [testResult, setTestResult]   = useState<{ success: boolean; message: string } | null>(null);
  const [loadingCals, setLoadingCals] = useState(false);
  const [calendars, setCalendars]     = useState<CalendarEntry[]>([]);
  const [showWizard, setShowWizard]   = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);

  // local edit state
  const [form, setForm] = useState({ ...status });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.admin.get("OutlookCalendar/Status");
      const data = await res.json();
      setStatus(data);
      setForm(data);
    } catch {
      /* safe default */
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await api.admin.post("OutlookCalendar/Config", {
        organizerEmail:       form.organizerEmail,
        organizerName:        form.organizerName,
        defaultDuration:      form.defaultDuration,
        titlePrefix:          form.titlePrefix,
        descriptionTemplate:  form.descriptionTemplate,
        meetingLinkTemplate:  form.meetingLinkTemplate,
        sendVisitorInvite:    form.sendVisitorInvite,
        sendChatConfirmation: form.sendChatConfirmation
      });
      if (!res.ok) throw new Error("Save failed");
      await load();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const res  = await api.admin.get(`OutlookCalendar/TestConnection?organizerEmail=${encodeURIComponent(form.organizerEmail)}`);
      const data = await res.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, message: "Test failed." });
    } finally {
      setTesting(false);
    }
  }

  async function loadCalendars() {
    if (!form.organizerEmail) return;
    setLoadingCals(true);
    try {
      const res  = await api.admin.get(`OutlookCalendar/Calendars?organizerEmail=${encodeURIComponent(form.organizerEmail)}`);
      const data = await res.json();
      setCalendars(Array.isArray(data.calendars) ? data.calendars : []);
    } catch {
      setCalendars([]);
    } finally {
      setLoadingCals(false);
    }
  }

  async function remove() {
    if (!confirm("Remove Outlook Calendar configuration? The bot will stop booking appointments.")) return;
    setDeleting(true);
    try {
      await api.admin.delete("OutlookCalendar/Config");
      await load();
    } catch { /* ignore */ }
    finally { setDeleting(false); }
  }

  function set(key: keyof typeof form, value: string | number | boolean) {
    setForm(f => ({ ...f, [key]: value }));
  }

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Microsoft" className="h-6 w-6" />
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Outlook Calendar Booking</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Let your bot book meetings directly on an Outlook calendar and send calendar invites to visitors.
          </p>
        </div>
      </div>

      {/* Calendar option callout */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 mb-5 text-xs text-slate-600">
        <p className="font-semibold text-slate-700 mb-1">Choosing a scheduling method</p>
        <p>
          <strong>Outlook Calendar</strong> books directly on your company's Outlook calendar — full control over availability, time blocks, buffer times, and per-rep routing.
          {" "}Visitors on Google receive a standard .ics invite that appears in Google Calendar automatically.
          {" "}If you prefer visitors to self-pick from your live availability, or your team uses Google Workspace without M365, use{" "}
          <a href="/integrations/calendly" className="text-blue-600 hover:underline">Calendly</a> instead.
          {" "}Both integrations can run simultaneously.
        </p>
      </div>

      {/* M365 prerequisite */}
      {!status.m365Connected && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6">
          <p className="text-sm font-semibold text-amber-800 mb-1">Microsoft 365 not connected</p>
          <p className="text-xs text-amber-700 mb-3">
            Outlook Calendar booking uses your existing Microsoft 365 connection. Connect M365 first, then come back here to configure calendar settings.
          </p>
          <a href="/integrations/microsoft-365">
            <SecondaryBlueButton label="Connect Microsoft 365" onClick={() => {}} />
          </a>
        </div>
      )}

      {/* Permission note */}
      {status.m365Connected && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 mb-5">
          <p className="font-semibold mb-1">One-time Azure permission required</p>
          <p>
            To read and write Outlook calendars, the Velaro Azure app needs <code className="bg-blue-100 px-1 rounded">Calendars.ReadWrite</code> application permission.
            {" "}If booking tests fail, ask your Azure admin to add this permission in{" "}
            <strong>Azure Portal → App Registrations → Velaro Admin → API Permissions</strong>.
          </p>
        </div>
      )}

      {/* Status */}
      {status.m365Connected && (
        <div className="flex items-center gap-2 mb-5">
          <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${status.configured ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-400"}`}>
            {status.configured ? "✓" : "–"}
          </span>
          <span className="text-sm text-slate-600">
            {status.configured
              ? `Configured — booking on ${status.organizerEmail}`
              : "Not yet configured — fill in the form below"}
          </span>
          {status.configured && (
            <button
              className="ml-auto text-xs text-blue-600 hover:underline"
              onClick={() => setShowWizard(true)}
            >
              Launch Starter Wizard
            </button>
          )}
        </div>
      )}

      {/* Config form */}
      {status.m365Connected && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
          <p className="text-sm font-semibold text-slate-700 mb-4">Calendar configuration</p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Organizer email <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-slate-400 mb-1">
                Meetings are created on this person's Outlook calendar. They must be a user in your M365 tenant.
              </p>
              <div className="flex gap-2">
                <input
                  className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="demos@yourcompany.com"
                  value={form.organizerEmail}
                  onChange={e => set("organizerEmail", e.target.value)}
                />
                <SecondaryBlueButton label={loadingCals ? "Loading..." : "Browse calendars"} onClick={loadCalendars} disabled={loadingCals || !form.organizerEmail} />
              </div>
              {calendars.length > 0 && (
                <div className="mt-2 rounded border border-slate-200 divide-y divide-slate-100 max-h-32 overflow-y-auto">
                  {calendars.map(c => (
                    <div key={c.id} className="px-3 py-1.5 text-xs flex items-center justify-between">
                      <span className="text-slate-700">{c.name}{c.isDefaultCalendar && " (default)"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Organizer display name</label>
              <input
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Velaro Sales Team"
                value={form.organizerName}
                onChange={e => set("organizerName", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Default meeting duration</label>
              <select
                className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.defaultDuration}
                onChange={e => set("defaultDuration", Number(e.target.value))}
              >
                {DURATION_OPTIONS.map(d => (
                  <option key={d} value={d}>{d} minutes</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Meeting title prefix</label>
              <p className="text-xs text-slate-400 mb-1">Combined with visitor name: e.g. "Demo with Jane Smith"</p>
              <input
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Demo with"
                value={form.titlePrefix}
                onChange={e => set("titlePrefix", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Meeting description / body</label>
              <textarea
                rows={3}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="This meeting was booked via Velaro chat."
                value={form.descriptionTemplate}
                onChange={e => set("descriptionTemplate", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Meeting link (Teams / Zoom / Meet)</label>
              <p className="text-xs text-slate-400 mb-1">Optional — appended to the meeting body.</p>
              <input
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="https://teams.microsoft.com/l/meetup-join/..."
                value={form.meetingLinkTemplate}
                onChange={e => set("meetingLinkTemplate", e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={form.sendVisitorInvite}
                  onChange={e => set("sendVisitorInvite", e.target.checked)}
                />
                Send calendar invite to visitor
              </label>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={form.sendChatConfirmation}
                  onChange={e => set("sendChatConfirmation", e.target.checked)}
                />
                Confirm booking in chat after creating event
              </label>
            </div>
          </div>

          {saveError && <p className="mt-3 text-xs text-red-600">{saveError}</p>}

          <div className="flex gap-2 mt-5">
            <PrimaryBlueButton label={saving ? "Saving..." : "Save configuration"} onClick={save} disabled={saving || !form.organizerEmail} />
            <SecondaryBlueButton label={testing ? "Testing..." : "Test connection"} onClick={testConnection} disabled={testing} />
            {status.configured && (
              <button
                className="ml-auto text-xs text-slate-400 hover:text-red-500"
                onClick={remove}
                disabled={deleting}
              >
                {deleting ? "Removing..." : "Remove"}
              </button>
            )}
          </div>

          {testResult && (
            <div className={`mt-3 rounded px-3 py-2 text-xs ${testResult.success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {testResult.success ? "✓ " : "✗ "}{testResult.message}
            </div>
          )}
        </div>
      )}

      {/* Appointment types */}
      {status.m365Connected && (
        <AppointmentTypesSection api={api} globalOrganizerEmail={status.organizerEmail} />
      )}

      {/* Workflow integration */}
      {status.configured && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
          <p className="text-sm font-semibold text-slate-700 mb-3">Using this in a workflow</p>
          <p className="text-xs text-slate-500 mb-4">
            Add an <strong>HTTP node</strong> to any workflow and point it at the booking endpoint below.
            The bot collects visitor info via <em>Ask Question</em> nodes, then POSTs to book the appointment.
          </p>
          <div className="bg-slate-900 rounded-lg p-3 text-xs text-slate-100 font-mono mb-3">
            <div className="text-slate-400 mb-1">// POST /api/admin/OutlookCalendar/Book</div>
            {`{
  "visitorName":     "<<visitorName>>",
  "visitorEmail":    "<<visitorEmail>>",
  "startTimeUtc":    "<<preferredTime>>",
  "durationMinutes": 30,
  "notes":           "<<visitorNotes>>"
}`}
          </div>
          <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
            Response includes <code>confirmationMessage</code> — send it directly to the visitor with a Send Message node.
          </div>
          <div className="mt-4">
            <PrimaryBlueButton label="Launch Booking Workflow Wizard" onClick={() => setShowWizard(true)} />
          </div>
        </div>
      )}

      {/* How it works */}
      <HowItWorks m365Connected={status.m365Connected} />

      {/* Availability rules explainer */}
      {status.m365Connected && <HowAvailabilityWorks />}

      {/* Booking history */}
      {status.configured && <BookingHistory api={api} />}

      {/* Sample rule flows */}
      {status.configured && <SampleRuleFlows />}

      {/* Wizard */}
      {showWizard && (
        <StarterWorkflowWizard
          integration="outlook-calendar"
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="OutlookCalendar" />
      </div>
    </div>
  );
}

// ── How it works ───────────────────────────────────────────────────────────────

function HowItWorks({ m365Connected }: { m365Connected: boolean }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How does Outlook Calendar booking work?</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 space-y-4 text-sm text-slate-600 mt-4">
          <ol className="space-y-3">
            {[
              {
                n: "1",
                title: "Bot collects visitor details",
                body: "The workflow uses Ask Question nodes to collect the visitor's name, email, and preferred meeting time."
              },
              {
                n: "2",
                title: "HTTP node POSTs to /OutlookCalendar/Book",
                body: "The collected variables are sent to the booking endpoint. Velaro calls the Microsoft Graph API to create the event on the organizer's calendar."
              },
              {
                n: "3",
                title: "Calendar invite sent",
                body: "Microsoft sends a calendar invite to both the organizer and the visitor (if Send Invite is on). The event appears in Outlook immediately."
              },
              {
                n: "4",
                title: "Bot confirms in chat",
                body: "The API response includes a confirmation message you can send to the visitor. The booking is complete — no manual steps."
              }
            ].map(step => (
              <li key={step.n} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold mt-0.5">
                  {step.n}
                </span>
                <div>
                  <p className="font-medium text-slate-700">{step.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          {!m365Connected && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800">
              <strong>M365 not connected.</strong> Go to{" "}
              <a href="/integrations/microsoft-365" className="underline">Integrations → Microsoft 365</a>{" "}
              to connect first.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sample rule flows ──────────────────────────────────────────────────────────

function SampleRuleFlows() {
  const [open, setOpen] = useState(false);

  const flows = [
    {
      icon: "fa-calendar-check",
      title: "Booking confirmation email",
      description: "After the Book Appointment node succeeds, add a Set Data node to tag the conversation 'appointment-booked'. Then create a Rule: when tag 'appointment-booked' is applied → trigger Email Campaign 'Booking Confirmation'. Include the date, time, meeting link, and an iCal Add-to-Calendar button.",
      rule: `Trigger: label_applied (appointment-booked) → Action: Email Campaign "Booking Confirmation"`,
      detail: "The booking node saves the event details. Pass <<bookingConfirmation>> to a Send Message node, and <<bookingEventId>> to a Set Data node as a contact property for cancel flows."
    },
    {
      icon: "fa-bell",
      title: "24-hour reminder",
      description: "Create a Scheduled Workflow that runs daily. Query BookedAppointments for meetings starting in ~24 hours. For each, send an email/SMS reminder: 'Your demo with [Company] is tomorrow at [time]. Click to join: [link]. Reply CANCEL to cancel.'",
      rule: `Scheduled Workflow: daily 8am UTC → query upcoming appointments → send reminder`,
      detail: "API: GET /OutlookCalendar/BookingHistory?status=active returns all active bookings with StartTimeUtc. Filter client-side or extend the endpoint to accept a date range."
    },
    {
      icon: "fa-star",
      title: "Post-meeting satisfaction survey",
      description: "Create a Scheduled Workflow that fires 2 hours after appointment end time. Send: 'How did your meeting go? Reply 1–5 (1=poor, 5=excellent).' When the visitor replies with a number, a rule captures it and saves it as a contact attribute. Low scores trigger an alert to the sales team.",
      rule: `Scheduled: 2h after EndTimeUtc → Send survey → Rule: incoming_message + contact tag "survey-sent" → parse rating → set contact.satisfactionScore`,
      detail: "Requires inbound message processing. See TODO: inbound-email-todo.md for the full cancel/reply flow architecture (ACS Event Grid webhook)."
    },
    {
      icon: "fa-calendar-xmark",
      title: "Cancel via reply",
      description: "When a visitor replies 'cancel' or 'reschedule' to a reminder, a Rule fires the cancel workflow: reads the eventId from the contact property, calls DELETE /OutlookCalendar/CancelEvent?eventId={eventId}, sends confirmation in chat, and updates the contact attribute.",
      rule: `Rule: incoming_message contains "cancel" AND contact has tag "appointment-booked" → HTTP DELETE /api/admin/OutlookCalendar/CancelEvent?eventId=<<contact.bookingEventId>>`,
      detail: "The CancelEvent endpoint cancels the Outlook Calendar event, sends cancellation notices to all attendees, and marks the BookedAppointment record as 'cancelled'."
    },
    {
      icon: "fa-envelope-open-text",
      title: "Reschedule offer",
      description: "After a cancellation (or a no-show detection), immediately offer to reschedule: send a Calendly link for a new slot. The Send Calendly Link workflow node creates a fresh single-use link and sends it in chat.",
      rule: `After CancelEvent succeeds → Send Calendly Link node → "Would you like to reschedule? Here's a new booking link: <<bookingUrl>>"`,
      detail: "Chain: CancelEvent HTTP node → Send Calendly Link node → Send Message. The visitor clicks the link and picks a new time from your live availability."
    },
    {
      icon: "fa-rotate",
      title: "No-show follow-up",
      description: "If your CRM or meeting platform flags the visitor as a no-show (meeting time passed + no activity), send a 'Sorry we missed you' message with a Calendly reschedule link. Route to the sales agent for manual follow-up.",
      rule: `Scheduled: 15 min after StartTimeUtc + status = "active" → Send: "We missed you! Want to reschedule?" + Calendly link`,
      detail: "Uses BookedAppointments.StartTimeUtc to detect the no-show window. Requires a scheduled workflow querying active appointments past their start time."
    }
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>Sample automation flows for appointments</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 mt-4 space-y-4">
          {flows.map((flow, i) => (
            <div key={i} className="flex gap-3">
              <span className="flex-shrink-0 mt-0.5 flex items-center justify-center h-8 w-8 rounded-lg bg-blue-50 text-blue-600 text-sm">
                <i className={`fa-solid ${flow.icon}`} />
              </span>
              <div>
                <p className="text-sm font-medium text-slate-700">{flow.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{flow.description}</p>
                <code className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 rounded mt-1.5 block leading-relaxed whitespace-pre-wrap">{flow.rule}</code>
                {"detail" in flow && flow.detail && (
                  <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-1 mt-1 leading-relaxed">{flow.detail}</p>
                )}
              </div>
            </div>
          ))}
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 mt-2">
            <strong>Build these in Rules Engine.</strong> Use the tag <code className="bg-blue-100 px-1 rounded">appointment-booked</code> on conversations when a booking is made, then trigger rules on that tag.
            {" "}<a href="/rules" className="underline">Open Rules</a>
          </div>
        </div>
      )}
    </div>
  );
}
